import { Link } from '@inertiajs/react';
import SdgIconStrip from '@/components/landing/sdg-icon-strip';
import { Calendar, ChevronRight } from 'lucide-react';

type SdgTag = {
    sdg_number: number;
    short_title: string;
    slug: string;
    image?: string | null;
};

type ContentFeatureCardProps = {
    title: string;
    image?: string | null;
    institute?: string | null;
    badges?: string[];
    description?: string | null;
    date?: string | null;
    linkHref?: string | null;
    linkLabel?: string;
    linkMode?: 'external' | 'internal';
    onLinkClick?: () => void;
    sdgs?: SdgTag[];
};

function formatDate(dateStr?: string | null): string {
    if (!dateStr) {
        return '';
    }

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
        return dateStr;
    }

    return date.toLocaleDateString('en-PH', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

export default function ContentFeatureCard({
    title,
    image,
    institute,
    badges = [],
    description,
    date,
    linkHref,
    linkLabel = 'View',
    linkMode = 'external',
    onLinkClick,
    sdgs = [],
}: ContentFeatureCardProps) {
    const visibleBadges = badges.filter(Boolean);
    const formattedDate = formatDate(date);

    return (
        <article className="group bg-card border-border flex h-full min-h-[390px] flex-col overflow-hidden rounded-[28px] border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
            {image ? (
                <div className="relative h-44 shrink-0 overflow-hidden">
                    <img
                        src={image}
                        alt={title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-white/10" />
                </div>
            ) : null}

            <div className="flex flex-1 flex-col p-5 md:p-6">
                {institute ? (
                    <p className="mb-3 line-clamp-2 text-xs leading-tight font-bold tracking-[0.18em] text-[#0A7A35] uppercase">{institute}</p>
                ) : null}

                {visibleBadges.length > 0 ? (
                    <div className="mb-4 flex flex-wrap gap-2">
                        {visibleBadges.map((badge) => (
                            <span
                                key={badge}
                                className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[11px] font-bold tracking-wider text-emerald-600 uppercase dark:text-[#21d36b]"
                            >
                                {badge}
                            </span>
                        ))}
                    </div>
                ) : null}

                <h3 className="text-foreground mb-3 line-clamp-3 text-[1.1rem] leading-tight font-extrabold group-hover:text-[#0A7A35]">
                    {title}
                </h3>

                {description ? <p className="text-muted-foreground mb-4 line-clamp-3 text-sm leading-relaxed">{description}</p> : null}

                <SdgIconStrip sdgs={sdgs} className="mb-5 flex flex-wrap gap-2.5" iconClassName="h-8 w-8 rounded-md" />

                <div className="border-border/60 text-muted-foreground mt-auto flex items-center justify-between border-t pt-4 text-sm">
                    <span className="flex items-center gap-2">
                        <Calendar className="text-muted-foreground/80 size-4" />
                        {formattedDate}
                    </span>

                    {linkHref ? (
                        linkMode === 'internal' ? (
                            <Link
                                href={linkHref}
                                prefetch
                                onClick={onLinkClick}
                                className="inline-flex items-center gap-1.5 font-bold text-[#0A7A35] transition-colors hover:text-[#08632b] hover:underline"
                            >
                                {linkLabel}
                                <ChevronRight className="size-4" />
                            </Link>
                        ) : (
                            <a
                                href={linkHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={onLinkClick}
                                className="inline-flex items-center gap-1.5 font-bold text-[#0A7A35] transition-colors hover:text-[#08632b] hover:underline"
                            >
                                {linkLabel}
                                <ChevronRight className="size-4" />
                            </a>
                        )
                    ) : null}
                </div>
            </div>
        </article>
    );
}
